import { useRef } from 'react';
import { Link, useRouteContext } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { useTranslation } from '@/lib/i18n';
import { Eyebrow } from '../primitives';
import { buildCampaignHref } from '../portal-href';

export type RecentCampaign = {
	id: string;
	name: string;
	coverImage: string;
	economicGoal?: number;
	collectedCents: number;
	/** The merchant's public donor portal. */
	portalId: string;
	slug: string;
	ngo?: { id: string; name?: string; logo?: { location: string } };
};

/**
 * Shared with the pending card. From `lg` an exact third of the track, so never a sliver of a fourth; below
 * it a fixed 340px, where the peeking next card says the row scrolls.
 */
const CARD_SHELL =
	'flex w-[340px] shrink-0 snap-start flex-col overflow-hidden rounded-2xl bg-white shadow-[0_8px_24px_0_rgba(77,33,0,0.06)] lg:w-[calc((100%-5rem)/3)]';

/** Always two lines tall, or the section's height depends on the newest campaign's name, which a placeholder cannot know. */
const CARD_TITLE = 'line-clamp-2 min-h-14 text-lg font-semibold';

/**
 * A plain `<a>`: the portal is another origin (`correos.worldcoo.com`), and the backend drops campaigns whose
 * merchant has none. A new tab, like the logo wheel and the partner-NGO modal, so the landing page stays.
 */
function CampaignCard(props: { campaign: RecentCampaign; baseUrl: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const campaign = props.campaign;
	const raised = Math.round(campaign.collectedCents / 100);
	const goal = campaign.economicGoal;
	const pct = goal && goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : undefined;

	return (
		<a
			href={buildCampaignHref(props.baseUrl, campaign.portalId, campaign.slug)}
			target="_blank"
			rel="noopener noreferrer"
			className={`${CARD_SHELL} transition-shadow hover:shadow-[0_12px_32px_0_rgba(77,33,0,0.12)]`}
		>
			<div className="relative aspect-[16/10] w-full overflow-hidden">
				<Image
					src={campaign.coverImage}
					alt={campaign.name}
					height={220}
					aspectRatio={16 / 10}
					objectFit="cover"
					className="absolute inset-0 h-full w-full object-cover!"
				/>
			</div>
			<div className="flex flex-1 flex-col gap-4 p-6">
				{campaign.ngo?.logo && (
					<Image
						src={campaign.ngo.logo.location}
						alt={campaign.ngo.name ?? ''}
						height={28}
						aspectRatio={4}
						objectFit="contain"
						className="h-6 w-auto max-w-28 object-contain!"
					/>
				)}
				<h3 className={CARD_TITLE}>{campaign.name}</h3>
				<div className="mt-auto flex flex-col gap-2">
					{/* Whole euros: a ",00" on every card is noise at this size. */}
					<p className="text-sm">
						<span className="text-brand-strong font-semibold">{formatNumber(language, raised, 0)} €</span>
						{goal ? (
							<span className="text-ink/50">
								{' '}
								{t`of`} {formatNumber(language, goal, 0)} €
							</span>
						) : null}
					</p>
					{pct !== undefined && (
						<div className="bg-ink/10 h-1 overflow-hidden rounded-full">
							<div className="bg-brand h-full rounded-full" style={{ width: `${pct}%` }} />
						</div>
					)}
					<span className="sr-only">{t`(opens in a new tab)`}</span>
				</div>
			</div>
		</a>
	);
}

/** Every dimension comes from `CampaignCard`'s constants and classes, so it measures the same. */
function PendingCard() {
	return (
		<article className={CARD_SHELL} aria-hidden>
			<div className="bg-ink/5 aspect-[16/10] w-full" />
			<div className="flex flex-1 flex-col gap-4 p-6">
				<div className="bg-ink/5 h-6 w-28 rounded" />
				<div className={CARD_TITLE}>
					<div className="bg-ink/5 h-4 w-full rounded" />
					<div className="bg-ink/5 mt-2 h-4 w-2/3 rounded" />
				</div>
				<div className="mt-auto flex flex-col gap-2">
					<div className="bg-ink/5 h-5 w-32 rounded" />
					<div className="bg-ink/10 h-1 rounded-full" />
				</div>
			</div>
		</article>
	);
}

// What the `lg` track shows.
const PENDING_CARDS = [0, 1, 2];

/**
 * Omit `campaigns` while they stream and it renders at its final size, which makes `<RecentCampaigns />` its
 * own `Suspense` fallback; a placeholder elsewhere would copy this geometry. Loaded and empty renders nothing.
 */
export function RecentCampaigns(props: { campaigns?: RecentCampaign[]; ngoCount?: number }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const { baseUrl } = useRouteContext({ from: '__root__' });
	const scrollerRef = useRef<HTMLDivElement>(null);

	// Measured: card width and gap both change at `lg`, so a constant step parks the row mid-card.
	const scrollByCards = (dir: 1 | -1) => {
		const scroller = scrollerRef.current;
		if (!scroller) return;
		const [first, second] = scroller.children;
		if (!first) return;
		const step = second
			? second.getBoundingClientRect().left - first.getBoundingClientRect().left
			: first.getBoundingClientRect().width;
		scroller.scrollBy({ left: dir * step, behavior: 'smooth' });
	};

	const campaigns = props.campaigns;
	const pending = campaigns === undefined;
	if (campaigns?.length === 0) return null;

	// lingui disallows calls inside t``. The dash keeps the line count without stating an unreal number.
	const ngoCountLabel = props.ngoCount === undefined ? '—' : formatNumber(language, props.ngoCount, 0);

	return (
		<section className="mx-auto max-w-[1440px] px-6 py-16 md:py-24 xl:px-10">
			<div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center">
				<Eyebrow>{t`Partner NGOs`}</Eyebrow>
				<h2 className="text-3xl font-medium tracking-tight text-balance md:text-5xl">
					{t`The NGOs' most recent campaigns`}
				</h2>
				<p className="text-ink/70 max-w-2xl text-base md:text-lg">
					{t`We collaborate with ${ngoCountLabel} NGOs committed to social, environmental and humanitarian causes. Each one passes a strict selection and audit process to ensure the funds turn into real solutions.`}
				</p>
				<Link
					to="/www/partner-ngos"
					className="bg-brand hover:bg-brand-strong mt-2 flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-5 font-medium text-black transition-colors"
				>
					{t`See partner NGOs`}
					<span className="bg-ink grid size-6 place-items-center rounded-full text-white">
						<ArrowUpRight className="size-4" />
					</span>
				</Link>
			</div>

			{/* Padding, `scroll-px` and `relative` are each load-bearing: docs/notes/portals/recent-campaigns.md */}
			<div
				ref={scrollerRef}
				className="hide-scrollbar relative -mx-6 mt-8 flex snap-x scroll-px-6 gap-6 overflow-x-auto scroll-smooth px-6 pt-4 pb-8 lg:mx-0 lg:gap-10"
			>
				{pending
					? PENDING_CARDS.map((i) => <PendingCard key={i} />)
					: campaigns.map((campaign) => <CampaignCard key={campaign.id} campaign={campaign} baseUrl={baseUrl} />)}
			</div>

			<div className="mt-2 flex justify-center gap-3">
				<button
					type="button"
					onClick={() => scrollByCards(-1)}
					aria-label={t`Previous`}
					className="border-ink/20 hover:bg-ink/5 grid size-11 place-items-center rounded-full border transition-colors"
				>
					<ArrowLeft className="size-5" />
				</button>
				<button
					type="button"
					onClick={() => scrollByCards(1)}
					aria-label={t`Next`}
					className="border-ink/20 hover:bg-ink/5 grid size-11 place-items-center rounded-full border transition-colors"
				>
					<ArrowRight className="size-5" />
				</button>
			</div>
		</section>
	);
}
